import { Button, IconButton, Modal } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Popover, Progress, Row } from "antd";
import Loading from "components/Loading";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useCurrentUser } from "store/users/selectors";
import { setWorksheetView } from "store/worksheetView/action";
import { useWorksheetView } from "store/worksheetView/selector";
import styled from "styled-components";
import { spanish_verb_forms_for_worksheet_tense } from "utils/config/reference";
import { useAudio } from "utils/context/AudioProvider";
import CloseIconss from "../../../utils/assets/images/closes.svg";
import {  formatTimeSpent } from "../helper";
import { WorkSheetPrint } from "../printWorkSheet";
import FillInTheBlankType from "../type/fillInTheBlankType";
import MultiChoiceType from "../type/multiChoiceType";
import SelectWhatYouHear from "../type/selectWhatYouHear";
import TranslateTheSentenceType from "../type/translateTheSentenceType";
import FinalResultScreen from "./FinalResult";
import ResultScreen from "./ResultModal";
import { getModernImage } from "utils/config/reference";
import { handleUpdateWorksheetSubmission } from "store/activities/thunks";
import {
  useCurrentClass,
  useCurrentClassTeacher,
} from "store/classes/selectors";
import { handleListUserSubmissionsByClass } from "store/submissions/thunks";
import { v4 } from "uuid";
import { get } from "lodash";
import dayjs from 'utils/dayjsConfig';
import { useCollectionNotification } from "utils/methods/firebaseNotification";
import { useOrientation } from "components/hooks";
import { InfoCircleOutlined } from "@ant-design/icons";
import { formatTextForRemoveUnderScore } from "features/forms/assignment/fillInBlanks/helper";
import usePreloadAssets from "components/hooks/usePreloadAssets";
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import { WrongAnswerSound } from 'utils/assets/images';
import { useAlertActions } from 'components/UI/alertSakeBar/alertProvider';
import SentryWrap from 'components/Sentry';

const twoColors = {
  "0%": "#108ee9",
  "100%": "#87d068",
};

export default function PlayWorkSheet({
  page = "",
  handleNextWorksheet,
  isPreview = false,
  book_id = "",
  assignmentInfo,
  numberMasked = "",
}) {
  const dispatch = useDispatch();
  const modernImage = getModernImage();
  const wrongSoundUrl = WrongAnswerSound;
  const currentClassTeacher = useCurrentClassTeacher();
  const { createCollectionNotification } = useCollectionNotification();
  const currentUser = useCurrentUser();
  const { closeAlert } = useAlertActions();
  const [resultModal, setResultModal] = useState(false);
  const [isOpenFinalResult, setIsOpenFinalResult] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [navigationIndex, setNavigationIndex] = useState(0);
  const [timeSpent, setTimeSpent] = useState(0);
  const [selectAnswer, setSelectAnswer] = useState("");
  const [startTime, setStartTime] = useState(new Date().getTime());
  const [isCorrect, setIsCorrect] = useState(0);
  const worksheetSelector = useWorksheetView();
  const [questions, setQuestions] = useState(
    worksheetSelector?.questions?.length > 0 ? worksheetSelector?.questions : []
  );
  const [consecutiveCorrect, setConsecutiveCorrect] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [wrongSound] = useState(() => {
    const sound = new Audio(wrongSoundUrl);
    sound.volume = 0.15;
    return sound;
  });

  const [loading, setLoading] = useState({ type: "", value: false });
  const { playAudio, isPlaying, stopAudio } = useAudio();
  const [isAnswered, setIsAnswered] = useState(0);
  const [studentSubmission, setStudentSubmission] = useState([]);
  const currentClass = useCurrentClass();
  const autoSyncTimeoutRef = useRef(null);
  const autoSyncInFlightRef = useRef(false);
  const autoSyncDisabledRef = useRef(false);
  const skipAutoSyncOnceRef = useRef(false);
  const lastAutoSyncKeyRef = useRef("");
  const is_assigned =
    worksheetSelector && worksheetSelector?.assignmentInfo?.id;
  const isLandscape = useOrientation();
  const [preloadedAssets, setPreloadedAssets] = useState({ images: {}, sounds: {} });
  // Use our custom hook for preloading images and sounds
  usePreloadAssets(modernImage, setPreloadedAssets);
  useEffect(() => {
    if (isPreview) {
      setActiveIndex(worksheetSelector?.activeIndex);
    }
    setQuestions(worksheetSelector?.questions);
  }, [worksheetSelector?.questions, worksheetSelector?.open]);

  useEffect(() => {
    if (worksheetSelector?.open === true && !isPreview) {
      autoSyncDisabledRef.current = false;
      skipAutoSyncOnceRef.current = true;
      const resumeData = worksheetSelector?.resumeData;
      if (resumeData) {
        setActiveIndex(resumeData.activeIndex || 0);
        setStudentSubmission(resumeData.studentSubmission || []);
        setIsCorrect(resumeData.isCorrect || 0);
        setIsAnswered(resumeData.activeIndex || 0);
        setResultModal(false);
        setIsOpenFinalResult(false);
        setTimeSpent(0);
        setStartTime(new Date().getTime());
        setConsecutiveCorrect(0);
      } else {
        handleTryAgain();
      }
    }
  },[worksheetSelector?.open])

  useEffect(() => {
    return () => {
      clearTimeout(autoSyncTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    if (worksheetSelector?.open) {
      setStartTime(new Date().getTime());
    }
  }, [worksheetSelector?.open]);

  const sessionId = useMemo(() => worksheetSelector?.assignmentInfo?.session_id, [worksheetSelector]);
  const forGrade = useMemo(() => worksheetSelector?.for_grade, [worksheetSelector]);

  useEffect(() => {
    const onPageShow = (event) => {
      if (event.persisted) {
        dispatch(setWorksheetView({ open: false }));
      }
    };
    window.addEventListener("pageshow", onPageShow);
    return () => {
      window.removeEventListener("pageshow", onPageShow);
      dispatch(setWorksheetView({ open: false }));
    };
  }, [dispatch]);

  useEffect(() => {
    const currentUserRole = currentUser?.role;
    if (!worksheetSelector?.open) return;
    if (isPreview) return;
    if (currentUserRole !== "student") return;
    if (!sessionId || !forGrade) return;
    if (autoSyncDisabledRef.current) return;
    if (skipAutoSyncOnceRef.current) {
      skipAutoSyncOnceRef.current = false;
      return;
    }
    if (isOpenFinalResult) return;
    if (loading?.value) return;
    if (!studentSubmission || studentSubmission.length === 0) return;

    const studentSubmissionSnapshot = studentSubmission;
    const finishlaterValue = studentSubmissionSnapshot.length;
    const totalCorrectAnswers = studentSubmissionSnapshot.filter((a) => a?.isCorrect).length;
    const lastAnswerId = studentSubmissionSnapshot[finishlaterValue - 1]?.question_id;
    const syncKey = `${finishlaterValue}-${totalCorrectAnswers}-${lastAnswerId || ""}`;
    if (lastAutoSyncKeyRef.current === syncKey) return;

    clearTimeout(autoSyncTimeoutRef.current);
    autoSyncTimeoutRef.current = setTimeout(async () => {
      if (!worksheetSelector?.open) return;
      if (autoSyncDisabledRef.current) return;
      if (autoSyncInFlightRef.current) return;
      if (lastAutoSyncKeyRef.current === syncKey) return;

      autoSyncInFlightRef.current = true;
      try {
        await dispatch(
          handleUpdateWorksheetSubmission({
            worksheet_id: sessionId,
            answer: studentSubmissionSnapshot,
            class_id: currentClass.id,
            student_id: currentUser.id,
            finishlater: finishlaterValue,
            complete: false,
          })
        );
        await dispatch(handleListUserSubmissionsByClass(currentUser?.id, currentClass?.id));
        lastAutoSyncKeyRef.current = syncKey;
      } catch (error) {
      } finally {
        autoSyncInFlightRef.current = false;
      }
    }, 300);

    return () => clearTimeout(autoSyncTimeoutRef.current);
  }, [
    studentSubmission,
    worksheetSelector?.open,
    isPreview,
    currentUser?.id,
    currentUser?.role,
    currentClass?.id,
    sessionId,
    forGrade,
    isOpenFinalResult,
    loading?.type,
    loading?.value,
    dispatch,
  ]);

  const currentData = useMemo(() => {
    return questions[activeIndex];
  }, [activeIndex, questions]);

  const percant = useMemo(() => {
    if (questions.length <= 1) return 100;
    return Math.floor((activeIndex / (questions.length - 1)) * 100);
  }, [questions, activeIndex]);

  const endResult = useMemo(() => {
    return questions.length - 1 === activeIndex;
  }, [questions, activeIndex]);

  const sendNotifications = async (notificationData) => {
    try {
      createCollectionNotification(
        notificationData,
        get(currentClass, "id", "")
      );
    } catch (error) {
      console.log(error, "error");
    }
  };

  const handlerStudentSubmission = async (studentTimeSpent) => {
    if (
      currentUser &&
      currentUser.role == "student" &&
      worksheetSelector?.assignmentInfo?.session_id && worksheetSelector.for_grade
    ) {
      autoSyncDisabledRef.current = true;
      clearTimeout(autoSyncTimeoutRef.current);
      const worksheetAssignmentInfo =
        worksheetSelector && worksheetSelector?.assignmentInfo;
      const notificationData = {
        id: v4(),
        classId: get(currentClass, "id", ""),
        displayName: get(worksheetSelector, "title", ""),
        senderId: get(currentUser, "id", ""),
        receiversId: currentClassTeacher,
        modified: dayjs().toISOString(),
        data: {
          chapter: worksheetAssignmentInfo?.bookId,
          page: worksheetAssignmentInfo?.pageId,
          id: get(worksheetAssignmentInfo, "session_id", ""),
          assignmentType: 'worksheet'
        },
        message: `${get(currentUser, "firstName", "")} ${get(
          currentUser,
          "lastName",
          ""
        )} Submit this Grammar Drill`,
        type: "update_activity",
        userSeen: [],
      };
      await dispatch(
        handleUpdateWorksheetSubmission({
          worksheet_id: worksheetSelector?.assignmentInfo?.session_id,
          answer: studentSubmission,
          class_id: currentClass.id,
          student_id: currentUser.id,
          time_duration: formatTimeSpent(studentTimeSpent),
          total_correct_answer: isCorrect,
          total_wrong_answer: questions?.length - isCorrect,
          complete: true
        })
      );
      await dispatch(
        handleListUserSubmissionsByClass(currentUser?.id, currentClass?.id)
      );
      sendNotifications(notificationData);
      handleClose();
    }
  };

    const buttonClickSound = (isAnswerCorrect) => {
      if (isMuted) return;
      if (!isAnswerCorrect) {
        wrongSound.play();
      }
    };


  const handleActiveIndex = () => {
    if (endResult) {
      if (isPreview) {
        return;
      }
      const currentTime = new Date().getTime();
      const elapsedTime = currentTime - startTime;
      setTimeSpent(elapsedTime);
      setIsOpenFinalResult(true);
      handlerStudentSubmission(elapsedTime);
      return;
    }
    setActiveIndex(activeIndex + 1);
  };

  const handleSubmit = (e, value = null) => {
    const selectedData = value ? value : selectAnswer;
    try {
      if (selectedData?.length === 0) {
        return;
      }
      const checkHighlighted =
        currentData?.highlightWords?.map((h) => h?.text?.trim()).join(" ") ||
        [];
      const isCorrectData = currentData?.highlightWords
        ? selectedData?.trim() === formatTextForRemoveUnderScore(checkHighlighted)
        : selectedData?.trim() === currentData?.correct_answer?.trim();

      setStudentSubmission((prev) => [
        ...prev,
        {
          question_id: currentData?.id,
          isCorrect: isCorrectData,
          value: selectedData,
          type: currentData?.type,
        },
      ]);

      if (isCorrectData) {
        const newConsecutiveCorrect = consecutiveCorrect + 1;
        setConsecutiveCorrect(newConsecutiveCorrect);

        // if (newConsecutiveCorrect === 3) {
        //   setConsecutiveCorrect(0);
        // }

        setIsCorrect((prev) => prev + 1);
        setIsAnswered((prev) => prev + 1);
        setSelectAnswer("");
        handleActiveIndex();
        return;
      }

      setResultModal(true);
      setSelectAnswer("");
    } catch (error) {
      console.log(error);
    }
  };

  const handleClose = async () => {
    if (loading?.type === "submitLesson" && loading?.value === true) {
      return;
    }
    autoSyncDisabledRef.current = true;
    clearTimeout(autoSyncTimeoutRef.current);
    closeAlert();
    await dispatch(setWorksheetView({ open: false }));
    setResultModal(false);
    setActiveIndex(0);
    setIsOpenFinalResult(false);
    setTimeSpent(0);
    setStartTime(new Date().getTime());
    setIsCorrect(0);
    setIsAnswered(0);
    setQuestions([]);
    setStudentSubmission([]);
    setConsecutiveCorrect(0);
  };

  const handleTryAgain = () => {
    autoSyncDisabledRef.current = true;
    clearTimeout(autoSyncTimeoutRef.current);
    setResultModal(false);
    setActiveIndex(0);
    setIsOpenFinalResult(false);
    setTimeSpent(0);
    setStartTime(new Date().getTime());
    setIsCorrect(0);
    setIsAnswered(0);
    setStudentSubmission([]);
    setConsecutiveCorrect(0);
  };

  const handleFinishLater = async () => {
    if (loading?.type === "finishLater" && loading?.value === true) {
      return;
    }
    autoSyncDisabledRef.current = true;
    clearTimeout(autoSyncTimeoutRef.current);
    if (isPreview) {
      handleClose();
      return;
    }
    if (
      currentUser &&
      currentUser.role === "student" &&
      worksheetSelector?.assignmentInfo?.session_id &&
      worksheetSelector.for_grade
    ) {
      try {
        setLoading({ type: "finishLater", value: true });
        await dispatch(
          handleUpdateWorksheetSubmission({
            worksheet_id: worksheetSelector?.assignmentInfo?.session_id,
            answer: studentSubmission,
            class_id: currentClass.id,
            student_id: currentUser.id,
            finishlater: activeIndex,
            complete: false
          })
        );
        await dispatch(
          handleListUserSubmissionsByClass(currentUser?.id, currentClass?.id)
        );
        setLoading({ type: "finishLater", value: false });
      } catch (error) {
        console.log(error);
        setLoading({ type: "finishLater", value: false });
      }
    }
    handleClose();
  };


  const closeResultScreen = () => {
    setIsOpenFinalResult(false);
    setTimeSpent(0);
    setStartTime(new Date().getTime());
  };

  const playSound = async () => {
    if (currentData?.question_audio?.length > 0 && !isMuted) {
      playAudio(currentData?.question_audio);
    }
  };

  const handleNextLesson = async () => {
    try {
      if (isPreview) {
        return;
      }
      setLoading({ type: "nextLesson", value: true });
      await handleNextWorksheet();
      setActiveIndex(0);
      setIsCorrect(0);
      setIsAnswered(0);
      setTimeSpent(0);
      setStudentSubmission([]);
      setLoading({ type: "nextLesson", value: false });
    } catch (error) {
      console.log(error);
    }
  };
  useEffect(() => {
    if (isMuted) {
      stopAudio();
      wrongSound.pause();
      wrongSound.currentTime = 0;
    }
    wrongSound.volume = isMuted ? 0 : 0.15;
    Object.values(preloadedAssets.sounds || {}).forEach((el) => {
      if (!el || typeof el.pause !== "function") return;
      el.volume = isMuted ? 0 : 1;
      if (isMuted) {
        el.pause();
        el.currentTime = 0;
      }
    });
  }, [isMuted, preloadedAssets, stopAudio, wrongSound]);

  const toggleMute = () => setIsMuted((v) => !v);


  const data = spanish_verb_forms_for_worksheet_tense.find(
    (item) => item?.title?.toLowerCase() === currentData?.tense?.toLowerCase()
  );

  const isWorksheetMobileChrome = useMediaQuery("(max-width:700px)");

  const volumeToolbar = (
    <div className="grammerDrillVolumePrint">
      <IconButton onClick={handleClose} style={{ padding: 0 }} aria-label="Close worksheet">
        <img style={{ height: 20 }} src={CloseIconss} alt="" title="" />
      </IconButton>
      {currentUser?.role === "student" && !isPreview && is_assigned && worksheetSelector?.for_grade && (
        <FinishLaterButton
          onClick={handleFinishLater}
          disabled={loading?.type === "finishLater" && loading?.value === true}
        >
          {loading?.type === "finishLater" && loading?.value === true ? (
            <Loading size={14} white />
          ) : (
            "Finish Later"
          )}
        </FinishLaterButton>
      )}
      <div className="setupVolCstm">
        <IconButton
          type="button"
          onClick={toggleMute}
          style={{
            color: isMuted ? "#999" : "#000",
            padding: 4,
          }}
          aria-label={isMuted ? "Unmute" : "Mute"}
        >
          {isMuted ? <VolumeOffIcon /> : <VolumeUpIcon />}
        </IconButton>
        {currentUser?.role === "teacher" && (
          <IconButton style={{ padding: 0, marginLeft: 10 }} aria-label="Print worksheet">
            <WorkSheetPrint questions={questions} chapter={numberMasked} />
          </IconButton>
        )}
      </div>
    </div>
  );

  const progressBlock =
    !isPreview && (
      <ProgressHighlight>
        <ProgessSection className="worksheetRowCntrCstm">
          <Progress
            percent={percant}
            strokeColor={twoColors}
            type="line"
            size={[400, 20]}
            format={(percent) => `${percent}%`}
          />
        </ProgessSection>
      </ProgressHighlight>
    );

  const navBlock =
    currentUser?.role === "teacher" ? (
      <div className="prevNextBtns setupPrevNxtBtn">
        <StyledButton
          onClick={() => {
            if (activeIndex > 0) {
              setActiveIndex(activeIndex - 1);
              setNavigationIndex(navigationIndex - 1);
              setIsAnswered(activeIndex - 1);
            }
          }}
          disabled={activeIndex === 0}
          style={{ flex: "1 1 45%", color: "white" }}
        >
          Previous
        </StyledButton>
        <StyledButton
          onClick={() => {
            if (activeIndex < questions.length - 1) {
              setActiveIndex(activeIndex + 1);
              setNavigationIndex(navigationIndex + 1);
            }
          }}
          disabled={activeIndex === questions.length - 1}
          style={{ flex: "1 1 45%", color: "white" }}
        >
          Next
        </StyledButton>
      </div>
    ) : (
      <div className="studentBarEndBtn setupPrevNxtBtn">
        {!isPreview && (
          <TitleText color="black" className="circleTotalPrize">
            <CircleProgress>
              <CircleProgressBarInner
                size={100}
                type="circle"
                percent={percant}
                strokeColor={twoColors}
                format={(percent) => `${percent}%`}
              />
            </CircleProgress>
          </TitleText>
        )}
      </div>
    );

  const teacherCircleBlock =
    !isPreview && currentUser?.role === "teacher" ? (
      <TitleText color="black">
        <CircleProgress>
          <CircleProgressBar
            size={100}
            type="circle"
            percent={percant}
            strokeColor={twoColors}
            format={(percent) => `${percent}%`}
          />
        </CircleProgress>
      </TitleText>
    ) : null;

  const mainContent = (
    <RowContainer>
      <FormContainer>
        <Container>
          {currentData?.image?.length > 3 ? (
            <div
              className="playMidImg"
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "10px",
                marginBottom: "20px",
              }}
            >
              <img
                src={
                  currentData?.image?.length > 3 ? currentData?.image : null
                }
                style={{
                  width: "100%",
                  maxWidth: "100%",
                  height: "auto",
                  maxHeight:
                    typeof window !== "undefined" && window.innerWidth <= 700
                      ? "min(55vh, 420px)"
                      : "250px",
                  objectFit: "contain",
                }}
              />
            </div>
          ) : null}

          {currentData?.tense && (
            <Popover
              placement="top"
              overlayStyle={{ zIndex: 9999 }}
              overlayInnerStyle={{
                maxWidth: "350px",
                padding: "8px",
              }}
              title={
                <strong style={{ fontWeight: "bold" }}>{data?.label}</strong>
              }
              overlayClassName="popoverRemoveBorder"
              content={() => {
                return (
                  <div>
                    {data?.explanation || "Description not available"}
                    <div className="mt-10px">
                      <div
                        style={{
                          fontWeight: "bold",
                          paddingBottom: "5px",
                        }}
                      >
                        Example:{" "}
                      </div>
                      {Array.isArray(data?.example) &&
                        data.example.map((item, index) => (
                          <div key={index}>{item}</div>
                        ))}
                    </div>
                  </div>
                );
              }}
              trigger="hover"
            >
              <span
                style={{
                  color: "#6330b7",
                  fontWeight: "600",
                  fontSize: "18px",
                  cursor: "pointer",
                  width: "fit-content",
                  margin: "auto",
                  display: "flex",
                  alignItems: "center",
                  gap: "5px",
                  marginTop: "5px",
                }}
              >
                {currentData?.tense}
                <InfoCircleOutlined />
              </span>
            </Popover>
          )}
          {currentData?.type === "Fill_IN_THE_Blank" && (
            <FillInTheBlankType
              handleSubmit={handleSubmit}
              activeIndex={activeIndex}
              currentData={currentData}
              selectAnswer={selectAnswer}
              setSelectAnswer={setSelectAnswer}
              isPlaying={isPlaying}
              playSound={playSound}
              timeSpent={timeSpent}
              consecutiveCorrect={consecutiveCorrect}
              setConsecutiveCorrect={setConsecutiveCorrect}
              isCorrect={isCorrect}
              setLoading={setLoading}
              modernImage={modernImage}
              preloadedAssets={preloadedAssets}
              buttonClickSound={buttonClickSound}
            />
          )}

          {currentData?.type === "Select_Correct_Response" && (
            <MultiChoiceType
              handleSubmit={handleSubmit}
              activeIndex={activeIndex}
              currentData={currentData}
              setSelectAnswer={setSelectAnswer}
              isPlaying={isPlaying}
              playSound={playSound}
              timeSpent={timeSpent}
              consecutiveCorrect={consecutiveCorrect}
              setConsecutiveCorrect={setConsecutiveCorrect}
              isCorrect={isCorrect}
              setLoading={setLoading}
              modernImage={modernImage}
              preloadedAssets={preloadedAssets}
              buttonClickSound={buttonClickSound}
            />
          )}

          {currentData?.type === "Select_What_You_Hear" && (
            <SelectWhatYouHear
              handleSubmit={handleSubmit}
              activeIndex={activeIndex}
              currentData={currentData}
              setSelectAnswer={setSelectAnswer}
              isPlaying={isPlaying}
              playSound={playSound}
              timeSpent={timeSpent}
              consecutiveCorrect={consecutiveCorrect}
              setConsecutiveCorrect={setConsecutiveCorrect}
              isCorrect={isCorrect}
              setLoading={setLoading}
              modernImage={modernImage}
              preloadedAssets={preloadedAssets}
              buttonClickSound={buttonClickSound}
            />
          )}

          {currentData?.type === "Translate_Text" && (
            <TranslateTheSentenceType
              handleSubmit={handleSubmit}
              activeIndex={activeIndex}
              currentData={currentData}
              setSelectAnswer={setSelectAnswer}
              isPlaying={isPlaying}
              playSound={playSound}
              timeSpent={timeSpent}
              consecutiveCorrect={consecutiveCorrect}
              setConsecutiveCorrect={setConsecutiveCorrect}
              isCorrect={isCorrect}
              setLoading={setLoading}
              modernImage={modernImage}
              preloadedAssets={preloadedAssets}
              buttonClickSound={buttonClickSound}
            />
          )}
        </Container>
      </FormContainer>
      <ImageWrapper />
    </RowContainer>
  );

  return (
    <SentryWrap>
      <ResultScreen
        currentData={currentData}
        isOpenResult={resultModal}
        setIsOpenResult={setResultModal}
        handleActiveIndex={handleActiveIndex}
        isMuted={isMuted}
      />
      <FinalResultScreen
        page={page}
        isOpenFinalResult={isOpenFinalResult}
        setIsOpenFinalResult={closeResultScreen}
        timeSpent={formatTimeSpent(timeSpent)}
        handleTryAgain={handleTryAgain}
        correctAnswers={isCorrect}
        totalQuestions={questions?.length}
        handleNextWorksheet={handleNextLesson}
        setActiveIndex={setActiveIndex}
        is_assigned={is_assigned}
        is_student={currentUser?.role === "student"}
        is_submission={worksheetSelector?.has_submission}
        handleClose={handleClose}
      />
      <Modal
        open={worksheetSelector?.open}
        onClose={() => dispatch(setWorksheetView({ open: false }))}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
        disableBackdropClick
        disableEscapeKeyDown
      >
        <ModalContent
          className="worksheet-play-modal worksheetPlayMdlCstm"
          bgColor={"white"}
          isLandscape={isLandscape}
        >
          {loading?.type === "nextLesson" && loading?.value === true ? (
            <div
              style={{ height: "100%" }}
              className="w-full justify-center flex items-center"
            >
              <Loading size={30} />
            </div>
          ) : (
            <>
              {isWorksheetMobileChrome ? (
                <>
                  <WorksheetPlayHeader className="worksheet-play-mobile-header">
                    {volumeToolbar}
                  </WorksheetPlayHeader>
                  <WorksheetPlayBody
                    className="worksheet-play-mobile-body"
                    $mobileScroll
                  >
                    <div className="grammerDrillRowButtons grammerDrillSetUp worksheet-play-scroll-chrome">
                      {progressBlock}
                      {navBlock}
                    </div>
                    {teacherCircleBlock}
                    {mainContent}
                  </WorksheetPlayBody>
                </>
              ) : (
                <>
                  <Row className="worksheetPlayRowCstm">
                    <div className="grammerDrillRowButtons grammerDrillSetUp">
                      {volumeToolbar}
                      {progressBlock}
                      {navBlock}
                    </div>
                  </Row>
                  {teacherCircleBlock}
                  <WorksheetPlayBody
                    className="worksheet-play-mobile-body"
                    $mobileScroll={false}
                  >
                    {mainContent}
                  </WorksheetPlayBody>
                </>
              )}
            </>
          )}
        </ModalContent>
      </Modal>
    </SentryWrap>
  );
}

const ModalContent = styled.div`
  left: 50%;
  width: 100%;
  position: absolute;
  height: 100vh;
  top: 50%;
  transform: translate(-50%, -50%);
  background-color: ${(p) => p.bgColor || "#fff"};
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  overflow: auto;
  box-sizing: border-box;

  @media screen and (min-width: 769px) and (max-width: 1366px) {
    padding-top: 40px;
  }

  @media only screen and (max-width: 600px) {
    min-width: 90%;
    padding-top: 0;
    height: 100vh;
    max-height: 100vh;
  }

  @supports (height: 100dvh) {
    @media only screen and (max-width: 600px) {
      height: 100dvh;
      max-height: 100dvh;
    }
  }

  @media only screen and (max-width: 700px) {
    overflow: hidden;
  }
`;

const WorksheetPlayHeader = styled.div`
  flex-shrink: 0;
  width: 100%;
  box-sizing: border-box;
  @media only screen and (max-width: 700px) {
    position: sticky;
    top: 0;
    z-index: 1300;
    background-color: #fff;
    padding-top: max(6px, env(safe-area-inset-top, 0px));
    padding-left: max(10px, env(safe-area-inset-left, 0px));
    padding-right: max(10px, env(safe-area-inset-right, 0px));
    padding-bottom: 8px;
    box-shadow: 0 1px 0 rgba(0, 0, 0, 0.06);
  }
`;

const WorksheetPlayBody = styled.div`
  width: 100%;
  box-sizing: border-box;
  ${(p) =>
    p.$mobileScroll
      ? `
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    overflow-x: hidden;
    -webkit-overflow-scrolling: touch;
    padding-bottom: max(28px, env(safe-area-inset-bottom, 12px));
  `
      : `
    flex: 0 1 auto;
    min-height: auto;
    overflow: visible;
  `}
`;

const TitleText = styled.span`
  font-size: 18px;
  font-weight: 600;
  color: ${(p) => p.color || "black"};
`;

const ImageWrapper = styled.div`
  height: 20vh;
  background-image: url("https://imagedelivery.net/B62pqpMZqieDKhv80nP5iw/34829d20-34ae-44ec-6cc1-b9c47532ae00/public");
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  width: 100%;
  aspect-ratio: 4/3;
  border-radius: 8px;
`;

const FormContainer = styled.div`
  padding: 20px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const Container = styled.div`
  // width: 50%;
  @media only screen and (max-width: 768px) {
    width: 100%;
  }
`;

const RowContainer = styled(Row)`
  min-height: 73vh;
  padding: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  @media only screen and (max-width: 700px) {
    min-height: 0;
    align-items: flex-start;
    padding: 12px max(12px, env(safe-area-inset-left, 0px)) 20px
      max(12px, env(safe-area-inset-right, 0px));
  }
`;
const ProgessSection = styled.div`
  width: 437.5px;
  .ant-progress-bg {
    height: 25px !important;
  }
  @media only screen and (max-width: 768px) {
    width: 70%;
  }
`;
const ProgressHighlight = styled.div`
  background: #f3f4f6;
  border-radius: 16px;
  padding: 16px 24px;
  border: 1px solid rgba(0, 0, 0, 0.05);
  box-shadow: 0 6px 20px rgba(15, 23, 42, 0.08);
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 10px 0 20px;
  @media only screen and (max-width: 768px) {
    padding: 12px 16px;
  }
`;
const CircleProgress = styled.div`
  position: relative;
  .ant-progress-circle-gradient {
    width: 90px !important;
    height: 90px !important;
  }
  @media Screen and (max-width: 600px) {
    .ant-progress-circle-gradient {
      width: 40px !important;
      height: 40px !important;
      font-size: 14px !important;
    }
  }
`;
const CircleProgressBar = styled(Progress)`
  position: absolute;
  right: 30px;
  @media Screen and (max-width: 768px) {
    top: 30px;
  }
`;

const CircleProgressBarInner = styled(Progress)`
  // position: absolute;
  right: 30px;
  @media Screen and (max-width: 768px) {
    top: 30px;
  }
`;

const StyledButton = styled(Button)`
  background-color: ${(p) =>
    p.backgroundColor
      ? p.backgroundColor
      : p.isCorrect
      ? "rgb(180, 255, 219) !important"
      : "#1b85e5 !important"};
  padding: 10px 24px;
  font-size: 16px;
  border-radius: 20px;
  color: ${(p) => (p.color ? p.color : "black")};
  text-transform: none;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  border: none;
  width: 100%;
  height: 45px;
  border-radius: 20px !important;
  margin-bottom: 20px;
  &:hover {
    background-color: #a0e4a0;
  }
`;

const FinishLaterButton = styled(Button)`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
  color: white !important;
  padding: 8px 20px;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.3px;
  border-radius: 25px;
  text-transform: none;
  margin-left: 12px;
  min-width: 120px;
  height: 38px;
  border: none;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);
  transition: all 0.3s ease;
  &:hover {
    background: linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%) !important;
    box-shadow: 0 6px 20px rgba(102, 126, 234, 0.5);
    transform: translateY(-2px);
  }
  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 10px rgba(102, 126, 234, 0.4);
  }
  &:disabled {
    background: linear-gradient(135deg, #a5b4fc 0%, #c4b5fd 100%) !important;
    color: #fff !important;
    box-shadow: none;
    transform: none;
  }
  @media only screen and (max-width: 600px) {
    padding: 6px 14px;
    font-size: 12px;
    min-width: 100px;
    height: 34px;
    margin-left: 8px;
  }
`;
